import seed from '../../seed/documents.json'
import type { Localized } from '../content/site'

/**
 * A text file in the shell's home directory, authored as Markdown and edited
 * from the CRM. Named `SiteDocument` rather than `Document` so it cannot be
 * confused with the DOM global at a glance.
 */
export type SiteDocument = {
  name: string
  order: number
  body: Localized
}

type Unknown = Record<string, unknown>

const localized = (value: unknown): value is Localized =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Unknown).en === 'string' &&
  typeof (value as Unknown).ru === 'string'

const isDocument = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null) return false
  const item = value as Unknown
  return typeof item.name === 'string' && item.name.length > 0 && localized(item.body)
}

const normalize = (value: unknown): SiteDocument => {
  const item = value as Unknown
  return {
    name: item.name as string,
    order: typeof item.order === 'number' ? item.order : 0,
    body: item.body as Localized,
  }
}

const parse = (payload: unknown[]): SiteDocument[] => {
  // One broken row must not empty the whole home directory.
  const valid = payload.filter(isDocument)
  if (valid.length !== payload.length) {
    console.warn(`Dropped ${payload.length - valid.length} malformed document record(s)`)
  }
  return valid.map(normalize).sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))
}

/**
 * Bundled at build time rather than imported lazily like the project seed: the
 * virtual filesystem is built synchronously on the very first command, so an
 * async fallback would show an empty `~` for the first few hundred ms.
 */
export const bundledDocuments: SiteDocument[] = parse(seed.documents)

export async function getDocuments(signal?: AbortSignal): Promise<SiteDocument[]> {
  const response = await fetch('/api/documents', {
    headers: { Accept: 'application/json' },
    signal,
  })
  if (!response.ok) throw new Error(`Documents request failed: ${response.status}`)

  const data: unknown = await response.json()
  const payload = Array.isArray(data) ? data : (data as { documents?: unknown }).documents
  if (!Array.isArray(payload)) throw new Error('Invalid documents response')
  return parse(payload)
}
